/** Graph display settings, persisted per mode (global/local) in localStorage.
 * Force ranges follow the Obsidian sliders; the actual d3 values are derived
 * in the view (charge = -30*repel, distance = linkDistance*0.4). */

export interface GraphSettings {
  showOrphans: boolean;
  showUnresolved: boolean;
  showAttachments: boolean;
  /** 0..1 — d3 forceCenter strength. */
  centerForce: number;
  /** 0..20 — charge strength = -30 * value. */
  repelForce: number;
  /** 0..1 — multiplies d3's default per-link strength. */
  linkForce: number;
  /** 30..500 — d3 link distance = value * 0.4 (Quartz-style scale-down). */
  linkDistance: number;
  /** -3..3 — label fade threshold; higher = labels appear later when zooming. */
  textFade: number;
  /** 1..3 — local graph BFS depth. */
  depth: number;
}

export const FORCE_DEFAULTS = {
  centerForce: 0.5,
  repelForce: 10,
  linkForce: 1,
  linkDistance: 250,
} as const;

export const DEFAULT_GRAPH_SETTINGS: GraphSettings = {
  showOrphans: true,
  showUnresolved: true,
  showAttachments: false,
  ...FORCE_DEFAULTS,
  textFade: 0,
  depth: 1,
};

export const GLOBAL_GRAPH_SETTINGS_KEY = 'rbv.graph.global.v1';
export const LOCAL_GRAPH_SETTINGS_KEY = 'rbv.graph.local.v1';

const num = (v: unknown, lo: number, hi: number, fallback: number): number =>
  typeof v === 'number' && Number.isFinite(v) ? Math.min(Math.max(v, lo), hi) : fallback;

const bool = (v: unknown, fallback: boolean): boolean =>
  typeof v === 'boolean' ? v : fallback;

export function loadGraphSettings(key: string): GraphSettings {
  let raw: unknown = null;
  try {
    const s = localStorage.getItem(key);
    raw = s ? JSON.parse(s) : null;
  } catch {
    raw = null;
  }
  const o = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const d = DEFAULT_GRAPH_SETTINGS;
  return {
    showOrphans: bool(o.showOrphans, d.showOrphans),
    showUnresolved: bool(o.showUnresolved, d.showUnresolved),
    showAttachments: bool(o.showAttachments, d.showAttachments),
    centerForce: num(o.centerForce, 0, 1, d.centerForce),
    repelForce: num(o.repelForce, 0, 20, d.repelForce),
    linkForce: num(o.linkForce, 0, 1, d.linkForce),
    linkDistance: num(o.linkDistance, 30, 500, d.linkDistance),
    textFade: num(o.textFade, -3, 3, d.textFade),
    depth: Math.round(num(o.depth, 1, 3, d.depth)),
  };
}

export function saveGraphSettings(key: string, s: GraphSettings): void {
  try {
    localStorage.setItem(key, JSON.stringify(s));
  } catch {
    // Private-mode/quota failures are non-fatal; settings just won't persist.
  }
}
